import TipTapEditor from '@components/tiptap/TipTapEditor'
import { TextEditorVariable } from '@components/tiptap/tiptap-ui/variables-dropdown'
import { TextEditorLocalizableProvider } from '@contexts/TextEditorLocalizableContext'
import { useLocalizableInput } from '@hooks/useLocalizableInput'
import { AvailableLocale } from '@models/Lang'
import { Form, Input, Select, Space, Typography } from 'antd'
import { Rule } from 'antd/es/form'
import { NamePath } from 'antd/es/form/interface'
import { ReactNode, useMemo } from 'react'
import { langList, langsAsOptions, langsByCode } from '../../factories/Utils'

const { Text } = Typography

interface Props {
  name: NamePath
  label?: ReactNode
  rules?: Rule[]
  selectedLangCode?: AvailableLocale
  namePrefix?: string[]
  variables?: TextEditorVariable[]
  hideLangSelector?: boolean
  mode?: 'simple' | 'full'
}

export const LocalizedTextEditor = ({
  name,
  label,
  rules = [],
  selectedLangCode: selectedLangProps,
  namePrefix = [],
  variables,
  hideLangSelector = false,
  mode,
}: Props) => {
  const {
    selectedLangCode,
    setSelectedLangCode,
    formItemName,
    formInstance,
    value,
    showLangSelector,
    showLangFlag,
  } = useLocalizableInput({
    selectedLangProps,
    name,
    namePrefix,
    hideLangSelector,
  })

  const handleEditorChange = (value: string) => {
    formInstance.setFieldValue(formItemName(selectedLangCode), value)
  }

  const isRequired = useMemo(() => {
    return rules.some(
      (rule) => typeof rule !== 'function' && rule.required === true
    )
  }, [rules])

  return (
    <TextEditorLocalizableProvider variables={variables}>
      <Space orientation="vertical" className="w-full">
        <Space className="w-full space-between">
          <Text className={`${isRequired ? 'field-required' : ''}`}>
            {label}
          </Text>
          {showLangSelector ? (
            <Select
              value={selectedLangCode}
              onChange={setSelectedLangCode}
              style={{ width: 120 }}
              options={langsAsOptions}
            />
          ) : showLangFlag ? (
            // The header selector drives the language: show a passive flag so
            // the field still states which locale it edits.
            <>{langsByCode[selectedLangCode].flag}</>
          ) : null}
        </Space>

        <Form.Item
          name={formItemName(selectedLangCode)}
          rules={rules}
          noStyle
          key={selectedLangCode}
        >
          <TipTapEditor
            mode={mode}
            value={value}
            onChange={handleEditorChange}
          />
        </Form.Item>

        {langList
          .filter((lang) => lang.code !== selectedLangCode)
          .map((lang) => (
            <Form.Item
              key={lang.id}
              name={formItemName(lang.code)}
              hidden
              rules={rules}
            >
              <Input type="hidden" />
            </Form.Item>
          ))}
      </Space>
    </TextEditorLocalizableProvider>
  )
}

export type LocalizedTextEditorType = { TextEditor: typeof LocalizedTextEditor }
